// Pure helpers for week math + display formatting shared by both timesheet
// views. Kept outside React so the logic is unit-testable.

import {
  addIsoDays,
  HOURS_RANGE_MESSAGE,
  hasLoggedHours,
  isHoursInRange,
  isTimesheetEditable,
  isWithinDailyCap,
  MAX_HOURS_PER_DAY,
  mondayOf,
  sundayOf,
  TIMESHEET_STATUSES,
  type TimesheetLockStage,
  type TimesheetStatus,
  timesheetLockStageFor,
} from '@clockwise/shared';

/** Fallback monthly hours target when the user record has no override.
 *  173.33 = 40 hrs/wk × 52 wk/yr ÷ 12 mo/yr — the round-trip equivalent
 *  of the previous DEFAULT_HOURS_PER_WEEK = 40. */
export const DEFAULT_HOURS_PER_MONTH = (40 * 52) / 12;

/** Standard working day/week used by the timesheet over/under (variance)
 *  rows. Unlike the per-user `hoursPerMonth` capacity — which still drives
 *  the week-summary chip and the approvals targets — the variance row
 *  measures each weekday against a flat 8h/day (40h/week) standard. */
export const STANDARD_HOURS_PER_DAY = 8;
export const STANDARD_HOURS_PER_WEEK = STANDARD_HOURS_PER_DAY * 5;

/** Pure conversion: an "available hours per month" capacity → an
 *  effective weekly target for the timesheet UIs (week grid + per-day
 *  list). Uses the calendar-year factor so a month with 4 weeks vs
 *  4.4 weeks averages out across the year. */
export function weeklyFromMonthly(monthlyHours: number | string | null | undefined): number {
  const n = monthlyHours == null ? DEFAULT_HOURS_PER_MONTH : Number(monthlyHours);
  if (!Number.isFinite(n)) return (DEFAULT_HOURS_PER_MONTH * 12) / 52;
  return (n * 12) / 52;
}

/** Tailwind color class for an over/under-target delta, used by both the
 *  page header chip and the per-day totals row in the week grid. Weekends
 *  always render in muted to avoid yellow-flagging zero-hour Sat/Sun. */
export function deltaTextClass(delta: number, isWeekend = false): string {
  if (isWeekend) return 'text-muted-foreground';
  if (delta === 0) return 'text-muted-foreground';
  return delta > 0
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-amber-700 dark:text-amber-300';
}

// Week math now lives in `@clockwise/shared` so `@clockwise/ui`'s date-range
// widget can share this exact implementation for its `current_week` preset
// instead of carrying a second one that drifts. Re-exported here because ~27
// call sites across the timesheet and approvals views import them from this
// module, and the indirection is cheaper than churning all of them.
export { mondayOf, sundayOf };

/** Add `delta` days to a YYYY-MM-DD date and return YYYY-MM-DD. */
export const addDays = addIsoDays;

/** The 7 date strings Mon..Sun for the given week start (Monday). */
export function weekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/** "Apr 20" / "Apr 20, 2026". */
export function shortDate(date: string, opts: { withYear?: boolean } = {}): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: opts.withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  });
}

/** "Mon" / "Tue" / ... */
export function shortDayName(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

/** "Wednesday". */
export function longDayName(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}

/** Sum a numeric-string column. Returns a number. */
export function sumHours(values: ReadonlyArray<string | number>): number {
  return values.reduce<number>((acc, v) => acc + Number(v || 0), 0);
}

/** Default `billable` for a NEW entry on a day: inherit from the day's
 *  most-recently-created entry (sticky), else `fallback` (default `false`).
 *  Picks the max by `createdAt` in a single pass — the range query's array
 *  order is NOT guaranteed to be createdAt order, so trusting `.at(-1)` would
 *  be non-deterministic on ties. Fed the day's existing entries for the same
 *  (customer, product) by both the week grid (which holds one entry per cell,
 *  so it gathers the day's sibling entries explicitly) and the day-list draft
 *  card, so adding a second block inherits billable the same way in either
 *  surface. When there's no prior entry to inherit from, `fallback` decides —
 *  callers pass the customer-driven default so a new entry for an external
 *  customer starts billable (internal / customerless cases resolve to
 *  non-billable at the call site, not here). */
export function stickyBillable(
  entries: ReadonlyArray<{ createdAt: string; billable: boolean }>,
  fallback = false,
): boolean {
  let latest: { createdAt: string; billable: boolean } | null = null;
  for (const e of entries) {
    if (latest === null || e.createdAt > latest.createdAt) latest = e;
  }
  return latest?.billable ?? fallback;
}

/** Render a Timesheet.hours field for an inline editable input. The
 *  server returns numeric(6,2) as a fixed-decimal string ("4.00"); strip
 *  trailing zeros so the user sees "4" instead of "4.00", without
 *  changing precision. Empty / non-finite → "". */
export function plainHours(hours: string | number | null | undefined): string {
  if (hours == null || hours === '') return '';
  const n = Number(hours);
  if (!Number.isFinite(n)) return '';
  // `String(0.50)` is already `'0.5'` in JS, so no regex-strip is needed —
  // numeric Number rendering drops trailing zeros for us.
  return String(Math.round(n * 100) / 100);
}

/** True when `raw` is a plain positive decimal within the per-entry cap:
 *  digits with at most one dot, > 0 and ≤ MAX_HOURS_PER_ENTRY. Whitespace is
 *  trimmed first.
 *
 *  We validate rather than strip-as-you-type so the field keeps exactly what
 *  the user typed and a bad value surfaces as an inline error instead of being
 *  silently rewritten. This is also stricter than a bare `Number()` parse: it
 *  rejects letters ("8h"), signs ("-5"), multiple dots ("1.2.3"), a lone "."
 *  (no digit), and `Number()` quirks like hex ("0x1f") or scientific ("1e3").
 *
 *  Empty is NOT valid here — callers treat an empty field separately (drafts
 *  allow null hours). The range check defers to the shared bound so the field
 *  and the API can't disagree. */
export function isValidHoursInput(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^\d*\.?\d*$/.test(trimmed) || !/\d/.test(trimmed)) return false;
  return isHoursInRange(Number(trimmed));
}

/**
 * The inline validation error for a typed hours value, or null when it's
 * acceptable. Single source of truth for both timesheet editors (week-grid
 * popover + day-list cards) so they surface identical wording.
 *
 * `otherHoursOnDay` is the sum of the day's OTHER entries (excluding the one
 * being edited); pass 0 when no siblings exist. Empty input is acceptable here
 * — callers treat an empty field as "no hours" / clear, routed separately.
 *
 * Two failure modes, in order: a malformed or out-of-range single value
 * (non-numeric, ≤ 0, or > per-entry cap) → HOURS_RANGE_MESSAGE; an otherwise-
 * valid value that would push the day's total past the 24h cap → a message
 * naming what's already logged.
 */
export function hoursValidationError(raw: string, otherHoursOnDay: number): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!isValidHoursInput(trimmed)) return HOURS_RANGE_MESSAGE;
  const n = Number(trimmed);
  if (!isWithinDailyCap(otherHoursOnDay, n)) {
    return `This day already has ${plainHours(otherHoursOnDay)}h logged — ${plainHours(n)}h would exceed the ${MAX_HOURS_PER_DAY}h daily limit.`;
  }
  return null;
}

/** "9.5h" — trims trailing .0 and adds "h". Used everywhere a single cell renders. */
export function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours === 0) return '—';
  const rounded = Math.round(hours * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/\.?0+$/, '')}h`;
}

/** Signed delta vs expected: returns "+1.5h" / "-2h" / "0h". */
export function formatDelta(delta: number): string {
  if (delta === 0 || !Number.isFinite(delta)) return '0h';
  const sign = delta > 0 ? '+' : '';
  const rounded = Math.round(delta * 100) / 100;
  const display = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, '');
  return `${sign}${display}h`;
}

/** Day index 0..6 for a date within the given week (0 = Monday). */
export function dayIndex(date: string, weekStart: string): number {
  return Math.floor(
    (new Date(`${date}T00:00:00Z`).getTime() - new Date(`${weekStart}T00:00:00Z`).getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

export type RowKey = string;

/** One week-grid row: a (customer, engagement, product) triple with at most
 *  ONE entry per day column (0..6 = Mon..Sun). Generic over the entry type so
 *  this module stays free of the `Timesheet` client type. */
export interface WeekRow<E> {
  key: RowKey;
  customerId: string | null;
  productId: string | null;
  /** The engagement (SOW) this row's entries are attributed to, or null for
   *  unattributed time. Part of the row's IDENTITY: the same customer and
   *  activity billed to two engagements is two rows, because a single row
   *  could hold only one of them per day and the second entry would have
   *  nowhere to render. */
  engagementId: string | null;
  /** The service role this row's entries are logged under. Part of the row's
   *  identity: the same (customer, product) under two DIFFERENT roles is two
   *  rows ("AC · Adhoc Work" and "BK · Adhoc Work"), each billing at its own
   *  rate. Null for a fresh draft whose role isn't resolved yet, or a
   *  customerless/internal entry. */
  roleId: string | null;
  /** One entry per day column, or `null` for an empty day. A (customer,
   *  product, role) triple with several entries on the SAME day is split across
   *  multiple rows — one "slot" per collision — so every cell holds at most
   *  one entry and duplicate rows are first-class (shown individually rather
   *  than collapsed into a single multi-entry cell). See `buildServerRows`. */
  entriesByDay: Array<E | null>;
  isDraft: boolean;
}

/** The non-empty cells of a week row (each day holds ≤1 entry or null). Shared
 *  so consumers don't re-spell the `(e): e is E => e != null` type guard — a
 *  stray `.filter(Boolean)` would silently keep the `| null` in the type. */
export function rowEntries<E>(row: WeekRow<E>): E[] {
  return row.entriesByDay.filter((e): e is E => e != null);
}

/** A single day cell eligible to be swept by "Clear empty rows": a still-
 *  editable DRAFT carrying no logged hours — null (blank) or an explicit zero
 *  (`0` / `"0.00"`). Copy-last-week reposts rows with hours blanked, and those
 *  placeholder cells are exactly what clutters the week. Only `draft` qualifies:
 *  submitted/approved entries are immutable, and rejected/failed ones need
 *  individual attention, so neither is ever swept (mirroring the per-row trash).
 *  Reuses `hasLoggedHours` — the ONE definition of "carries hours worth acting
 *  on" — so this stays in lockstep with copy/submit's skip rules.
 *
 *  `opts.isLocked` excludes a cell whose date sits in a locked period: the
 *  server rejects a DELETE there (`assertDatesNotLocked`), so a locked blank
 *  draft must not be counted as clearable or the sweep would 422 on it. Callers
 *  pass `(e) => isEntryLocked(e, isDateLocked)`. */
export function isClearableEmptyEntry<
  E extends { status: TimesheetStatus; hours: string | number | null },
>(entry: E, opts?: { isLocked?: (entry: E) => boolean }): boolean {
  if (opts?.isLocked?.(entry)) return false;
  return entry.status === 'draft' && !hasLoggedHours(entry.hours);
}

/** A week-grid row whose EVERY entry is a clearable-empty draft (see
 *  {@link isClearableEmptyEntry}): it holds at least one entry and none carries
 *  logged hours, an immutable status, or a locked date (via `opts.isLocked`).
 *
 *  "Clear empty entries" sweeps at CELL granularity — {@link isClearableEmptyEntry}
 *  per cell — so this row-level predicate no longer decides what's swept. It
 *  survives to decide manual-line scaffold removal: when the sweep clears every
 *  cell a manual line owns, the now-empty scaffold is dropped too (mirroring the
 *  per-row trash button); a line that keeps any cell stays. A zero-entry scaffold
 *  (a freshly "Add row"-ed line) is NOT matched — it carries no persisted data
 *  and is governed by the unsaved-draft leave guard instead. */
export function isEmptyClearableRow<
  E extends { status: TimesheetStatus; hours: string | number | null },
>(row: WeekRow<E>, opts?: { isLocked?: (entry: E) => boolean }): boolean {
  const entries = rowEntries(row);
  return entries.length > 0 && entries.every((e) => isClearableEmptyEntry(e, opts));
}

/** Wrap `index` into `[0, length)`, handling negative values correctly (JS `%`
 *  keeps the sign, so a bare `index % length` can be negative). The circular-
 *  index helper shared by the week-view find stepper (page `stepFind`) and the
 *  grid's `openMatchAt`, so the two can't diverge on wrap-vs-clamp semantics.
 *  Returns 0 for a non-positive `length` — callers guard the empty case. */
export function normalizeIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

/** One find match: a specific non-empty day cell in a specific row. Keyed by
 *  `(rowKey, dayIndex)` so the grid can open exactly that cell's popover, plus
 *  the matched `entryId` so a find session can re-anchor after edits reorder or
 *  drop cells. */
export interface FindMatchCell {
  rowKey: RowKey;
  dayIndex: number;
  entryId: string;
}

/**
 * Ordered list of cells matching an active week-view find. A MATCH is a
 * NON-EMPTY entry cell whose row's customer OR activity name contains the
 * (case-insensitive) query AND whose status is in the selected set. An empty
 * `query` leaves the text half unconstrained; an empty `statuses` set leaves
 * the status half unconstrained; with BOTH empty there is nothing to find, so
 * the result is empty (prev/next disabled) rather than "every cell" — matching
 * browser-find intuition and avoiding auto-opening dozens of popovers.
 *
 * Iterates `orderedRows` (already in the grid's visual order — `displayRows`)
 * and, within each row, day columns 0→6 (Mon→Sun), so prev/next steps in the
 * on-screen reading order. Pure + unit-tested; the grid supplies the name
 * resolvers closing over its batched customer/product label maps.
 */
export function computeWeekFindMatches<E extends { id: string; status: TimesheetStatus }>(
  orderedRows: ReadonlyArray<WeekRow<E>>,
  opts: {
    query: string;
    statuses: ReadonlySet<TimesheetStatus>;
    customerName: (customerId: string | null) => string;
    activityName: (productId: string | null) => string;
  },
): FindMatchCell[] {
  const q = opts.query.trim().toLowerCase();
  const hasText = q.length > 0;
  const hasStatus = opts.statuses.size > 0;
  if (!hasText && !hasStatus) return [];

  const matches: FindMatchCell[] = [];
  for (const row of orderedRows) {
    const rowTextMatch =
      !hasText ||
      opts.customerName(row.customerId).toLowerCase().includes(q) ||
      opts.activityName(row.productId).toLowerCase().includes(q);
    if (!rowTextMatch) continue;
    for (let i = 0; i < row.entriesByDay.length; i++) {
      const e = row.entriesByDay[i];
      if (!e) continue;
      if (hasStatus && !opts.statuses.has(e.status)) continue;
      matches.push({ rowKey: row.key, dayIndex: i, entryId: e.id });
    }
  }
  return matches;
}

/**
 * Per-status entry counts for the find bar's status-dot summary, scoped to the
 * current TEXT query. Tallies every non-empty cell whose row's customer OR
 * activity name contains the (case-insensitive) `query` — the SAME text half as
 * `computeWeekFindMatches`, so a dot's count can't disagree with what stepping
 * actually finds. An empty `query` matches every row, so the counts are the full
 * week totals.
 *
 * The status SCOPE is deliberately NOT applied here: each dot reports how many
 * entries of its status match the text alone, so the dots stay independent
 * toggles (selecting "draft" must not zero — and thus hide — the other dots).
 * The result always carries a key for every `TimesheetStatus` (0 when none), so
 * the bar can drop the zeros itself.
 */
export function computeWeekFindStatusCounts<E extends { status: TimesheetStatus }>(
  orderedRows: ReadonlyArray<WeekRow<E>>,
  opts: {
    query: string;
    customerName: (customerId: string | null) => string;
    activityName: (productId: string | null) => string;
  },
): Record<TimesheetStatus, number> {
  const q = opts.query.trim().toLowerCase();
  const hasText = q.length > 0;
  const counts = Object.fromEntries(TIMESHEET_STATUSES.map((s) => [s, 0])) as Record<
    TimesheetStatus,
    number
  >;
  for (const row of orderedRows) {
    const rowTextMatch =
      !hasText ||
      opts.customerName(row.customerId).toLowerCase().includes(q) ||
      opts.activityName(row.productId).toLowerCase().includes(q);
    if (!rowTextMatch) continue;
    for (const e of row.entriesByDay) {
      if (e) counts[e.status] += 1;
    }
  }
  return counts;
}

/** Whether an entry is "locked" for row-level rebind purposes: its status is
 *  immutable (submitted/approved — i.e. not `isTimesheetEditable`) OR its date's
 *  period has locked. The week grid gates a row's customer/activity pickers on
 *  whether ANY of its entries is locked: rebinding moves only the row's drafts,
 *  so a row holding even one locked entry would be split (drafts move, locked
 *  ones strand) — it's rendered read-only instead.
 *
 *  This is the single home for the combined locked rule. The cell renderer
 *  (`TimesheetCell`) keeps the two halves apart — status-lock (`locked`) vs
 *  period-lock (`periodLocked`) — because it needs them separately (a
 *  period-locked cell stays openable read-only; a submitted cell stays openable
 *  to add a sibling), but its status half uses the same `isTimesheetEditable`
 *  rule consumed here, so the two can't drift on what "immutable status" means. */
export function isEntryLocked(
  entry: { status: TimesheetStatus; date: string; customerId: string | null },
  isDateLocked: (date: string, customerId: string | null, stage?: TimesheetLockStage) => boolean,
): boolean {
  if (!isTimesheetEditable(entry.status)) return true;
  // WHICH boundary this row closes on depends on the row: a `draft` is new
  // work and stops at the submission deadline, while a `rejected`/`failed`
  // row is a correction and stays open through settlement. Derived from the
  // SAME `timesheetLockStageFor` the API enforces with, so a cell can never
  // render editable and then 422 on save.
  return isDateLocked(entry.date, entry.customerId, timesheetLockStageFor('edited', entry.status));
}

/** Counts the Submit button + tooltip + success toast read off a week. */
export interface WeekSubmitPartition {
  /** Editable rows that carry hours (`> 0`) — what a Submit click will flip. */
  submittableCount: number;
  /** Editable rows with NO logged hours (null / 0 / "0.00") — skipped by
   *  submit, left as drafts. Drives the "N empty entries skipped" disclosure. */
  emptyEditableCount: number;
  /** Rows already `submitted` this week (not editable, not re-flipped). */
  alreadySubmittedCount: number;
  /** Editable rows carrying hours whose period has CLOSED for them — the
   *  server filters these out of the submit rather than failing on them, so
   *  they are excluded from `submittableCount` and disclosed separately.
   *  Non-zero only once a period has closed while its week is still on
   *  screen (including a week straddling an open and a closed period). */
  periodClosedCount: number;
  /** Whether at least one hours-bearing, still-open editable row exists — the
   *  gate for enabling the Submit button (an all-empty or fully-closed week
   *  has nothing to flip). */
  hasSubmittableInRange: boolean;
}

/**
 * Partition a week's entries the way Submit needs: how many editable rows will
 * actually flip, how many empty ones will be skipped, how many are already in.
 *
 * "Carries hours" is `hasLoggedHours` — the EXACT predicate the API's
 * `submitRange` partitions on — so the button's count and the "N skipped"
 * disclosure can never drift from what the server really submits. Null AND
 * zero (`0` / `"0.00"`) both count as empty on both sides.
 *
 * `opts.isLocked` is the period half of that same parity. `submitRange`
 * FILTERS rows whose period has closed for them out of its candidate set
 * (they are skipped, not fatal — a range can straddle an open period and a
 * closed one), so counting them here would promise a flip the server will not
 * perform. Omitting the predicate counts every row as still open, which is
 * correct for callers with no lock context (locking off, or a pure status
 * rollup).
 */
export function partitionWeekForSubmit<
  E extends { status: TimesheetStatus; hours: number | string | null },
>(entries: readonly E[], opts?: { isLocked?: (entry: E) => boolean }): WeekSubmitPartition {
  let submittable = 0;
  let empty = 0;
  let submitted = 0;
  let periodClosed = 0;
  for (const e of entries) {
    if (isTimesheetEditable(e.status)) {
      // Order matters: an empty row in a closed period is reported as closed,
      // not as "fill in the hours" — filling them in would not help.
      if (opts?.isLocked?.(e)) periodClosed += 1;
      else if (hasLoggedHours(e.hours)) submittable += 1;
      else empty += 1;
    } else if (e.status === 'submitted') submitted += 1;
  }
  return {
    submittableCount: submittable,
    emptyEditableCount: empty,
    alreadySubmittedCount: submitted,
    periodClosedCount: periodClosed,
    hasSubmittableInRange: submittable > 0,
  };
}

/** Group identity for a (customer, engagement, product) triple: a customer can
 *  appear under several products, and the same pair can be billed to several
 *  engagements, so all three are part of the key. Null = internal/unbound work
 *  (or, for the engagement, time nothing covers). The entry's activity role is
 *  a function of the product (`product.serviceRoleId`), so it is NOT an
 *  independent key dimension. NOTE: this is the GROUP key, not a row key — a
 *  group can yield several rows when a day holds more than one entry, and each
 *  row keys on its own representative entry id (see `buildServerRows`) for
 *  stable React identity across sibling edits. */
export function rowKeyFor(
  customerId: string | null,
  productId: string | null,
  engagementId: string | null = null,
): RowKey {
  return `${customerId ?? 'no-customer'}|${productId ?? 'none'}|${engagementId ?? 'no-engagement'}`;
}

/** The minimum entry shape `buildServerRows` needs to bucket and order rows.
 *  `timesheetEntryServiceRoleId` is the activity's own role — shown as the
 *  row's chip; it always equals the product's role, so it isn't part of the
 *  group key. */
interface WeekRowEntry {
  id: string;
  customerId: string | null;
  productId: string | null;
  engagementId: string | null;
  timesheetEntryServiceRoleId: string | null;
  date: string;
  createdAt: string;
}

/** (createdAt, id) ascending comparator — `id` breaks ties when bulk writes
 *  (e.g. imports) share a createdAt timestamp, keeping order deterministic. */
function byCreatedAtThenId(
  a: { createdAt: string; id: string },
  b: { createdAt: string; id: string },
): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Pack flat week entries into rows, one entry per cell.
 *
 * Entries are grouped by (customer, engagement, product). Within a group each day's
 * entries are ordered by `(createdAt, id)`; the group then yields as many rows
 * as its busiest day has entries. Row (slot) `s` takes the `s`-th entry of
 * every day, so a pair logged once per day stays a single row, and a day with
 * two entries pushes a second row holding the newer one. Because every new
 * entry is the newest-in-its-day, a "second block" accretes consistently into
 * the same slot row rather than reshuffling.
 *
 * Each row's `key` is its earliest entry's id (its "representative"), NOT the
 * positional slot — so deleting a sibling entry in another row doesn't shift
 * this row's key and force a React remount (which would tear down an open
 * popover / focus). Groups are ordered by their earliest entry, and a group's
 * slot rows stay adjacent in slot order, so a row keeps its position no matter
 * which cell the user fills next and new pairs sort to the bottom. The
 * backend's date-based ORDER BY is not relied on here.
 */
export function buildServerRows<E extends WeekRowEntry>(
  entries: E[],
  weekStart: string,
): WeekRow<E>[] {
  interface Group {
    customerId: string | null;
    productId: string | null;
    engagementId: string | null;
    roleId: string | null;
    byDay: E[][];
  }
  const groups = new Map<RowKey, Group>();
  for (const e of entries) {
    const idx = dayIndex(e.date, weekStart);
    if (idx < 0 || idx >= 7) continue; // outside the visible week
    // Group by (customer, engagement, product). The activity role (the row's
    // chip) follows the product, so it isn't part of the key — it's captured
    // from the first entry that carries it (a brand-new optimistic row may not
    // yet). The engagement IS part of the key: two entries for the same pair
    // under different engagements must not share a cell.
    const key = rowKeyFor(e.customerId, e.productId, e.engagementId);
    let g = groups.get(key);
    if (!g) {
      g = {
        customerId: e.customerId,
        productId: e.productId,
        engagementId: e.engagementId,
        roleId: e.timesheetEntryServiceRoleId,
        byDay: Array.from({ length: 7 }, () => [] as E[]),
      };
      groups.set(key, g);
    } else if (g.roleId == null) {
      g.roleId = e.timesheetEntryServiceRoleId;
    }
    g.byDay[idx]?.push(e);
  }

  // Sort every group's day buckets ONCE, then derive each group's order key
  // (its earliest entry) from the sorted heads — no separate insert-time
  // bookkeeping to keep in lockstep with the bucket sort.
  const decorated = Array.from(groups.values()).map((g) => {
    for (const bucket of g.byDay) bucket.sort(byCreatedAtThenId);
    let earliest: E | null = null;
    for (const bucket of g.byDay) {
      const head = bucket[0]; // each bucket is sorted, so head is its earliest
      if (head && (earliest === null || byCreatedAtThenId(head, earliest) < 0)) earliest = head;
    }
    return { g, earliest: earliest as E }; // groups always have ≥1 entry
  });
  decorated.sort((a, b) => byCreatedAtThenId(a.earliest, b.earliest));

  const rows: WeekRow<E>[] = [];
  for (const { g } of decorated) {
    const slots = Math.max(...g.byDay.map((b) => b.length)); // ≥1: group has ≥1 entry
    for (let s = 0; s < slots; s++) {
      const cells = g.byDay.map((bucket) => bucket[s] ?? null);
      // Representative = the slot row's earliest entry; a row at slot s always
      // has at least one cell (the busiest day reaches index s), so this is
      // never null. Used as the stable React key.
      let rep: E | null = null;
      for (const e of cells) {
        if (e && (rep === null || byCreatedAtThenId(e, rep) < 0)) rep = e;
      }
      rows.push({
        key: rep ? rep.id : `${rowKeyFor(g.customerId, g.productId, g.engagementId)}#${s}`,
        customerId: g.customerId,
        productId: g.productId,
        engagementId: g.engagementId,
        roleId: g.roleId,
        entriesByDay: cells,
        isDraft: false,
      });
    }
  }
  return rows;
}

/** A row the user explicitly added via "Add row" that has been kept SEPARATE
 *  for the editing session, rather than being auto-grouped by (customer,
 *  product) the way `buildServerRows` packs everything else.
 *
 *  Rows aren't persisted — `buildServerRows` regroups flat entries every
 *  render — so the only way a deliberately-added duplicate (same customer +
 *  activity as an existing row) stays its own row is for the grid to remember,
 *  in transient React state, which entry ids belong to it. That memory is this
 *  shape: the line's picked customer/product/role plus the ≤1 entry id it owns
 *  per day column (0..6 = Mon..Sun). It lives only while the week stays mounted;
 *  a reload or week-switch drops it and the entries merge back — the accepted
 *  trade-off for not persisting a line identity on the row.
 *
 *  A line with no claimed entries yet (all-null) is a freshly-added scaffold
 *  (the old "draft row"); once it owns an entry it renders like any saved row. */
export interface ManualLineState {
  key: RowKey;
  customerId: string | null;
  productId: string | null;
  /** The engagement the line is bound to, mirroring `WeekRow.engagementId`.
   *  A manual line is already kept separate by id, so this is what the line
   *  WRITES onto its entries rather than what distinguishes it. */
  engagementId: string | null;
  roleId: string | null;
  /** Entry ids this line owns, indexed by day column (0..6). `null` = empty
   *  day. At most one id per day, matching the one-entry-per-cell invariant. */
  claimedIdsByDay: ReadonlyArray<string | null>;
}

/** Every entry id currently claimed by a manual line. The grid feeds this to
 *  `buildServerRows` (as an exclusion set) so a claimed entry is rendered ONLY
 *  by its manual line, never also auto-packed into a (customer, product) row. */
export function claimedEntryIds(lines: ReadonlyArray<ManualLineState>): Set<string> {
  const ids = new Set<string>();
  for (const line of lines) {
    for (const id of line.claimedIdsByDay) if (id) ids.add(id);
  }
  return ids;
}

/** Turn manual lines into grid rows by resolving each claimed id to its live
 *  entry from `entriesById` (the range cache is the source of truth — the
 *  claimed id may have been swapped temp→real or deleted since it was claimed).
 *  A claimed id with no matching entry (deleted, or an optimistic create that
 *  rolled back) becomes an empty cell. `isDraft` is true only while the line
 *  owns nothing — so an empty scaffold sorts/trashes like the old draft row,
 *  and a populated line behaves like a saved row. */
export function buildManualLineRows<E extends { id: string }>(
  lines: ReadonlyArray<ManualLineState>,
  entriesById: ReadonlyMap<string, E>,
): WeekRow<E>[] {
  return lines.map((line) => {
    const entriesByDay = line.claimedIdsByDay.map((id) =>
      id ? (entriesById.get(id) ?? null) : null,
    );
    return {
      key: line.key,
      customerId: line.customerId,
      productId: line.productId,
      engagementId: line.engagementId,
      roleId: line.roleId,
      entriesByDay,
      isDraft: entriesByDay.every((e) => e == null),
    };
  });
}

/** Sentinel UUID used in optimistic-entry placeholder fields the client
 *  cannot know until the server returns the real row (tenantId, roleId).
 *  All-zero is intentionally visible so accidental reads stand out. */
export const OPTIMISTIC_PLACEHOLDER_UUID = '00000000-0000-0000-0000-000000000000';

/** True if `entry` is a client-side optimistic record (id prefixed `temp-`).
 *  Use this before reading approval-time fields like `serviceVersion` /
 *  `currency` or before showing audit metadata. */
export function isOptimisticEntry(entry: { id: string }): boolean {
  return entry.id.startsWith('temp-');
}

/** Mint a `temp-…` id for an optimistic cache write. */
export function newOptimisticId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Shape of inputs used by both the week grid and day list to mint an
 *  optimistic timesheet entry. customerId and hours are nullable so the
 *  day-view auto-save can fire on any single field. */
export interface OptimisticTimesheetInput {
  customerId: string | null;
  productId: string | null;
  /** The engagement the caller resolved for this cell, when it resolved one.
   *  Part of the row key, so an optimistic entry that omitted it would render
   *  in a DIFFERENT row than the one it lands in when the server replies. */
  engagementId?: string | null;
  /** The activity's own role, when the caller knows it (the grid has the
   *  picked product's `serviceRoleId`). Shown as the row chip immediately;
   *  the server response fills it on resolve. Grouping is by (customer,
   *  product), so a null here doesn't reshuffle rows. */
  timesheetEntryServiceRoleId?: string | null;
  date: string;
  hours: number | null;
  description: string | null;
  billable: boolean;
}

/** Build a Timesheet-shaped object for the cache. The two unknown UUIDs
 *  use OPTIMISTIC_PLACEHOLDER_UUID; the real server response replaces this
 *  row in the mutation's onSuccess. */
export function optimisticTimesheet(
  tempId: string,
  userId: string,
  input: OptimisticTimesheetInput,
): {
  id: string;
  tenantId: string;
  customerId: string | null;
  userId: string;
  customerTeamServiceRoleId: string | null;
  timesheetEntryServiceRoleId: string | null;
  date: string;
  hours: string | null;
  productId: string | null;
  engagementId: string | null;
  serviceId: string | null;
  serviceVersion: number | null;
  description: string | null;
  currency: string | null;
  billable: boolean;
  status: 'draft';
  resolutionReason: string | null;
  createdAt: string;
  updatedAt: string;
} {
  const now = new Date().toISOString();
  return {
    id: tempId,
    tenantId: OPTIMISTIC_PLACEHOLDER_UUID,
    customerId: input.customerId,
    userId,
    // Customer-team role is resolved server-side (and may prompt) — unknown
    // optimistically. The activity role is shown immediately when the caller
    // passes it; grouping is by (customer, product) so null doesn't reshuffle.
    customerTeamServiceRoleId: null,
    timesheetEntryServiceRoleId: input.timesheetEntryServiceRoleId ?? null,
    date: input.date,
    hours: input.hours == null ? null : String(input.hours),
    productId: input.productId,
    engagementId: input.engagementId ?? null,
    serviceId: null,
    serviceVersion: null,
    description: input.description,
    currency: null,
    billable: input.billable,
    status: 'draft',
    resolutionReason: null,
    createdAt: now,
    updatedAt: now,
  };
}
